import { createContext, useContext, useEffect, useRef, useState } from 'react';

export const RecipePhotoContext = createContext(null);
export const PHOTO_BUCKET = 'recipe-photos';
export const photoPath = (tenant, recipe) => `${tenant}/${encodeURIComponent(recipe)}.jpg`;

export async function compressRecipePhoto(file) {
  if (!file || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new Error('Kies een JPG-, PNG- of WebP-foto. Exporteer een HEIC-foto eerst als JPG.');
  }
  if (file.size > 20 * 1024 * 1024) throw new Error('Deze foto is groter dan 20 MB. Kies een kleinere foto.');
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Deze foto kan niet worden geopend. Kies een andere foto.'));
      image.src = url;
    });
    const ratio = Math.min(1, 1200 / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Foto verkleinen lukt niet op dit apparaat.');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    let blob;
    for (const quality of [0.82, 0.7, 0.58, 0.45]) {
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (!blob) throw new Error('Foto verkleinen is mislukt. Probeer een andere foto.');
      if (blob.size <= 250 * 1024) break;
    }
    if (blob.size > 350 * 1024) throw new Error('Deze foto blijft te groot. Kies een eenvoudigere of kleinere uitsnede.');
    return blob;
  } finally { URL.revokeObjectURL(url); }
}

export default function RecipePhoto({ recipeId, name, readOnly = false }) {
  const settings = useContext(RecipePhotoContext);
  const [saved, setSaved] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const generation = useRef(0);
  const input = useRef(null);
  const client = settings?.client;
  const tenant = settings?.tenant;
  const editable = settings?.canEdit && !readOnly;
  const path = photoPath(tenant, recipeId);
  useEffect(() => {
    const version = ++generation.current;
    setSaved(null); setPending(null); setLoaded(false); setMessage(''); setBusy(false);
    if (client && tenant && recipeId) {
      client.storage.from(PHOTO_BUCKET).download(path).then(({ data, error }) => {
        if (version !== generation.current) return;
        if (error && !['400', '404'].includes(String(error.statusCode || error.status))) {
          setMessage('Foto laden lukt niet. Open het recept opnieuw om te proberen.');
        } else setLoaded(true);
        if (data) setSaved(data);
      }).catch(() => { if (version === generation.current) setMessage('Foto laden lukt niet. Probeer het recept opnieuw te openen.'); });
    }
    return () => { generation.current++; };
  }, [client, tenant, recipeId, path]);
  const [url, setUrl] = useState('');
  useEffect(() => {
    const blob = pending || saved;
    if (!blob) { setUrl(''); return; }
    const next = URL.createObjectURL(blob); setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [pending, saved]);
  if (!client || !tenant) return null;
  const choose = async e => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    const version = generation.current;
    setBusy(true); setMessage('Foto wordt verkleind…');
    try {
      const blob = await compressRecipePhoto(file);
      if (version === generation.current) { setPending(blob); setMessage('Controleer de foto en klik op Foto opslaan.'); }
    } catch (error) { if (version === generation.current) setMessage(error.message); }
    finally { if (version === generation.current) setBusy(false); }
  };
  const save = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      if (!(await settings.authorize('een receptfoto op te slaan'))) return;
      const { error } = await client.storage.from(PHOTO_BUCKET).upload(path, pending, { upsert: true, contentType: 'image/jpeg', cacheControl: '0' });
      if (error) throw error;
      setSaved(pending); setPending(null); setMessage('Foto opgeslagen.');
      window.dispatchEvent(new CustomEvent('kc-recipe-photo-changed', { detail: { tenant, recipeId, blob: pending } }));
    } catch { setMessage('Foto opslaan is niet gelukt. Je vorige foto blijft behouden. Probeer opnieuw.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (busy || !window.confirm('Deze receptfoto verwijderen?')) return;
    setBusy(true);
    try {
      if (!(await settings.authorize('een receptfoto te verwijderen'))) return;
      const { error } = await client.storage.from(PHOTO_BUCKET).remove([path]);
      if (error) throw error;
      setSaved(null); setPending(null); setMessage('Foto verwijderd.');
      window.dispatchEvent(new CustomEvent('kc-recipe-photo-changed', { detail: { tenant, recipeId, blob: null } }));
    } catch { setMessage('Foto verwijderen is niet gelukt. Probeer opnieuw.'); }
    finally { setBusy(false); }
  };
  if (!editable && !url) return null;
  const button = { minHeight: 44, padding: '8px 14px', border: '1px solid #b7ccc2', borderRadius: 8, background: '#f0f7f3', color: '#18503f', cursor: 'pointer', font: 'inherit' };
  return <section aria-label="Receptfoto" style={{ marginBottom: 24 }}>
    {url && <button type="button" aria-label={expanded ? 'Foto verkleinen' : 'Foto vergroten'} onClick={() => setExpanded(!expanded)} style={{ display: 'block', border: 0, padding: 0, background: 'transparent', marginBottom: 10, maxWidth: '100%', cursor: 'zoom-in' }}>
      <img src={url} alt={`Receptfoto: ${name}`} style={{ display: 'block', width: expanded ? 900 : 240, maxWidth: '100%', maxHeight: expanded ? '65vh' : 180, objectFit: 'contain', borderRadius: 10 }} />
    </button>}
    {editable && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Receptfoto kiezen" onChange={choose} hidden />
      <button type="button" style={button} disabled={busy || !loaded} onClick={() => input.current.click()}>{saved ? 'Foto vervangen' : 'Foto toevoegen'}</button>
      {pending && <><button type="button" style={button} disabled={busy} onClick={save}>Foto opslaan</button><button type="button" style={button} disabled={busy} onClick={() => { setPending(null); setMessage(''); }}>Annuleren</button></>}
      {saved && !pending && <button type="button" style={button} disabled={busy} onClick={remove}>Foto verwijderen</button>}
    </div>}
    {editable && <p style={{ color: '#526771', fontSize: 13 }}>Eén foto per recept. We verkleinen je foto automatisch; het origineel wordt niet opgeslagen.</p>}
    {message && <p role="status" style={{ fontSize: 14 }}>{message}</p>}
  </section>;
}

 
// Private photos are fetched only as rows approach the scroll viewport.
export function RecipeThumbnail({ recipeId }) {
  const settings = useContext(RecipePhotoContext);
  const client = settings?.client;
  const tenant = settings?.tenant;
  const host = useRef(null);
  const [visible, setVisible] = useState(false);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!host.current) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '80px' });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    let active = true;
    let revision = 0;
    setBlob(null);
    const changed = event => {
      if (event.detail?.tenant === tenant && event.detail?.recipeId === recipeId) {
        revision++;
        setBlob(event.detail.blob);
      }
    };
    window.addEventListener('kc-recipe-photo-changed', changed);
    if (visible && client && tenant) {
      const startedAt = revision;
      client.storage.from(PHOTO_BUCKET).download(photoPath(tenant, recipeId))
        .then(({ data, error }) => { if (active && revision === startedAt) setBlob(error ? null : data); })
        .catch(() => { /* Missing/offline images keep the neutral placeholder. */ });
    }
    return () => { active = false; window.removeEventListener('kc-recipe-photo-changed', changed); };
  }, [client, tenant, recipeId, visible]);
  useEffect(() => {
    if (!blob) { setUrl(''); return; }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return <span ref={host} aria-hidden="true" style={{ width: 52, height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 8, background: '#edf3f0', border: '1px solid #dce6e0', color: '#739184' }}>
    {url ? <img src={url} alt="" decoding="async" onError={() => setBlob(null)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> :
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 6c-3-2-6-2-9-1v14c3-1 6-1 9 1 3-2 6-2 9-1V5c-3-1-6-1-9 1Zm0 0v14" /></svg>}
  </span>;
}

